import { Edit, Article } from '@mui/icons-material';
import { Grid, IconButton, Slide } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';

import { TableReload } from '#app/Action/TableAction';
import { dispatch } from '#app/App';
import { cacheTablePage, cacheTableServer } from '#app/Cache';
import { hasRight } from '#app/Common/UserCommon';
import ContentViewComponent from '#app/Component/ContentViewComponent';
import IconAction from '#app/Component/IconAction';
import SelectComponent from '#app/Component/SelectComponent';
import ShadowBoxComponent from '#app/Component/ShadowBoxComponent';
import TableComponent from '#app/Component/TableComponent';
import TextFieldComponent from '#app/Component/TextFieldComponent';
import TitleComponent from '#app/Component/TitleComponent';
import ApiHandler from '#app/Handler/ApiHandler';
import { initFormHandler } from '#app/Handler/FormHandler';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import useCountries from '#app/hooks/useCountries';

function BuyPurveyorList() {
  const authenticationReducer = useSelector((state) => state.AuthenticationReducer);
  const [optionsPurveyorType, setOptionsPurveyorType] = React.useState([]);
  const { countries } = useCountries();

  const columns = [
    {
      name: 'id',
      label: 'Référence',
      options: { filter: true, sort: true },
    },
    {
      name: 'name',
      label: 'Nom du fournisseur',
      options: { filter: true, sort: true },
    },
    {
      name: 'address',
      label: 'Adresse',
      options: { filter: true, sort: true },
    },
    {
      name: 'purveyorType',
      label: 'Type de fournisseur',
      options: { filter: true, sort: true },
    },
    {
      name: 'paymentTerms',
      label: 'Conditions de paiement',
      options: { filter: true, sort: true },
    },
    {
      name: 'productionTime',
      label: 'Délai de production + livraison',
      options: { filter: true, sort: true },
    },
    {
      name: 'deliveryQuantityVariation',
      label: 'Variation des quantités livrées',
      options: { filter: true, sort: true },
    },
    {
      name: 'transport',
      label: 'Franco de port',
      options: { filter: true, sort: true },
    },
    {
      name: 'divalto',
      label: 'Référence Divalto',
      options: { filter: true, sort: true },
    },
    {
      name: 'country',
      label: 'Pays',
      options: { filter: true, sort: true },
    },
    {
      name: 'action',
      label: 'Action',
      options: { filter: false, sort: false },
    },
  ];

  // formSearch
  const [formSearch, setFormSearch] = React.useState({
    name: {
      name: 'name',
      label: 'Nom',
      textHelper: 'Rechercher par noms de fournisseur.',
      type: 'text',
      defaultValue:
        cacheTableServer.api_buy_purveyor_list && cacheTableServer.api_buy_purveyor_list.name
          ? cacheTableServer.api_buy_purveyor_list.name
          : '',
    },
    purveyorType: {
      name: 'purveyorType',
      label: 'Type de fournisseur',
      textHelper: 'Rechercher par types de fournisseur.',
      type: 'integer',
      defaultValue:
        cacheTableServer.api_buy_purveyor_list && cacheTableServer.api_buy_purveyor_list.purveyorType
          ? cacheTableServer.api_buy_purveyor_list.purveyorType
          : 0,
    },
    country: {
      name: 'country',
      label: 'Pays',
      textHelper: 'Rechercher par pays.',
      type: 'integer',
      defaultValue:
        cacheTableServer.api_buy_purveyor_list && cacheTableServer.api_buy_purveyor_list.country
          ? cacheTableServer.api_buy_purveyor_list.country
          : 0,
    },
  });
  const handlerFormSearch = initFormHandler(formSearch, setFormSearch);
  const inputSearchChange = (index, value) => {
    handlerFormSearch.setValue(index, value);
    cacheTablePage.api_buy_purveyor_list = 0;
    cacheTableServer.api_buy_purveyor_list.page = 0;
    cacheTableServer.api_buy_purveyor_list[index] = value;
    dispatch(TableReload('api_buy_purveyor_list'));
  };
  const getAction = (row) => {
    if (
      hasRight(authenticationReducer, 'ROLE_BUY_PRODUCT') ||
      hasRight(authenticationReducer, 'ROLE_BUY_PRODUCT_GET')
    ) {
      return (
        <>
          {hasRight(authenticationReducer, 'ROLE_BUY_PURVEYOR') ||
          hasRight(authenticationReducer, 'ROLE_BUY_PURVEYOR_EDIT') ? (
            <IconAction title="Modifier" icon="edit" to={getRoutePathname('buy_purveyor', { id: row.id })} />
          ) : hasRight(authenticationReducer, 'ROLE_BUY_PURVEYOR_GET') ? (
            <IconAction title="Visualiser" icon="view" to={getRoutePathname('buy_purveyor', { id: row.id })} />
          ) : (
            <IconButton disabled={true}>
              <Edit style={{ color: '#17a2b8' }} />
            </IconButton>
          )}
        </>
      );
    } else {
      return (
        <>
          {hasRight(authenticationReducer, 'ROLE_BUY_PRODUCT_EDIT') ? (
            <IconButton style={{ cursor: 'no-drop' }}>
              <Edit />
            </IconButton>
          ) : (
            <IconButton style={{ cursor: 'no-drop' }}>
              <Article />
            </IconButton>
          )}
        </>
      );
    }
  };

  React.useEffect(() => {
    handlerFormSearch.start();

    // get options
    ApiHandler.get({ route: 'api_select_purveyor_type' }, (response) => {
      const data = [{ value: 0, label: 'Tous' }, ...response.data];
      setOptionsPurveyorType(data);
    });
  }, []);

  return (
    <ContentViewComponent
      breadcrumbs={{
        title: 'Liste des fournisseurs',
        context: 'Achat',
      }}
    >
      <Slide direction="left" in timeout={750}>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <ShadowBoxComponent>
            <TitleComponent title={'Filtres'} />
            <Grid container spacing={1}>
              <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
                <TextFieldComponent
                  id={'name'}
                  handler={handlerFormSearch}
                  onChange={(val) => inputSearchChange('name', val)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <SelectComponent
                  id={'purveyorType'}
                  options={optionsPurveyorType}
                  handler={handlerFormSearch}
                  onChange={(val) => inputSearchChange('purveyorType', val)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={4} xl={4}>
                <SelectComponent
                  withSelectAll
                  id={'country'}
                  options={countries}
                  handler={handlerFormSearch}
                  onChange={(val) => inputSearchChange('country', val)}
                />
              </Grid>
            </Grid>
          </ShadowBoxComponent>
        </Grid>
      </Slide>

      <br />

      <TableComponent
        id={'api_buy_purveyor_list'}
        title={'Fournisseurs'}
        columns={columns}
        actionFirst={
          hasRight(authenticationReducer, 'ROLE_BUY_PURVEYOR') ||
          hasRight(authenticationReducer, 'ROLE_BUY_PURVEYOR_ADD')
            ? {
                label: 'Ajouter un fournisseur',
                link: getRoutePathname('buy_purveyor', { id: 'add' }),
              }
            : false
        }
        promiseServerData={(resolve, options) => {
          const datas = handlerFormSearch.getData();
          const filters = {
            limit: options.rowsPerPage,
            offset: options.page * options.rowsPerPage,
            sortName: options.sortOrder.name,
            sortDirection: options.sortOrder.direction,
            name: formSearch.name.value ? formSearch.name.value : '',
            purveyorType: datas.purveyorType === 0 || !datas.purveyorType ? '' : datas.purveyorType,
            country: datas.country,
          };
          ApiHandler.get(
            {
              route: 'api_buy_purveyor_list',
              data: filters,
            },
            (response) => {
              const data = response.data.data;
              for (const index in data) {
                data[index].action = getAction(data[index]);
                data[index].deliveryQuantityVariation = `${data[index].deliveryQuantityVariation}%`;
                if (data[index].transport === 1) {
                  data[index].transport = 'Oui';
                } else {
                  data[index].transport = 'Non';
                }
              }
              resolve(data, response.data.count);
            },
          );
        }}
      />

      <br />
    </ContentViewComponent>
  );
}

export default BuyPurveyorList;
